import { Card } from "@rothoblaas/design-system";
import classNames from "classnames";
import { FC, useEffect, useState } from "react";
import useI18n from "../../../hooks/useTranslations";
import { BeamReinforcementResult } from "../../../http/beamReinforcement/formulas";
import Beam3DDrawing from "./Beam3DDrawing";
import styles from "./SectionDrawingCard.module.css";

type SectionDrawingCardProps = {
  result: BeamReinforcementResult | null;
  existingLength?: number | null;
  reinforcementLength?: number | null;
  className?: string;
};

const EXISTING_COLOR = "#efd8a8";
const EXISTING_STROKE = "#cfae6e";
const REINFORCEMENT_COLOR = "#e0c799";
const REINFORCEMENT_STROKE = "#b99a5f";
const OK_COLOR = "#5f8f3a";
const BAD_COLOR = "#e3002b";
const DIM_COLOR = "#5c5c5c";

const VIEW_WIDTH = 320;
const VIEW_HEIGHT = 190;
const MARGIN_X = 44;
const MARGIN_Y = 28;

const SectionDrawingCard: FC<SectionDrawingCardProps> = ({
  result,
  existingLength,
  reinforcementLength,
  className,
}) => {
  const i18n = useI18n();
  const [viewMode, setViewMode] = useState<"2d" | "3d">("2d");

  const can3D = !!existingLength && existingLength > 0 && !!reinforcementLength && reinforcementLength > 0;

  useEffect(() => {
    if (!can3D && viewMode === "3d") {
      setViewMode("2d");
    }
  }, [can3D, viewMode]);

  if (!result) {
    return (
      <Card style={{ minHeight: 400 }} className={classNames(styles.preview, className)} accentColor="var(--preview-border)" shadow title={i18n.translation.common.preview}>
        <div className={styles.empty}>{i18n.translation.beamReinforcementModule.results.incompleteWarning}</div>
      </Card>
    );
  }

  if (can3D && viewMode === "3d") {
    return (
      <Card style={{ minHeight: 400 }} className={classNames(styles.preview, className)} accentColor="var(--preview-border)" shadow title={i18n.translation.common.preview}>
        <div className={styles.wrapper}>
          <div className={styles.viewToggle}>
            <button type="button" className={styles.viewToggleButton} onClick={() => setViewMode("2d")}>
              {i18n.translation.beamReinforcementModule.drawing.view2D}
            </button>
            <button type="button" className={classNames(styles.viewToggleButton, styles.viewToggleButtonActive)}>
              {i18n.translation.beamReinforcementModule.drawing.view3D}
            </button>
          </div>
          <Beam3DDrawing result={result} existingLength={existingLength} reinforcementLength={reinforcementLength} />
          <div className={styles.legend}>
            <span>
              <span className={styles.legendSwatch} style={{ background: EXISTING_COLOR }} />
              {`(1) ${result.existing.name}`}
            </span>
            <span>
              <span className={styles.legendSwatch} style={{ background: REINFORCEMENT_COLOR }} />
              {`(2) ${result.reinforcement.name}`}
            </span>
          </div>
        </div>
      </Card>
    );
  }

  const { b1, h1, b2, h2, n1, screwOK, eBottom, eTop2 } = result;
  const totalWidth = n1 === 2 ? b2 + b1 + b2 : b1 + b2;
  const scale = Math.min((VIEW_WIDTH - 2 * MARGIN_X) / totalWidth, (VIEW_HEIGHT - 2 * MARGIN_Y) / h1);
  const t = (v: number): number => v * scale;

  const yCenter = MARGIN_Y + t(h1) / 2;
  const y1Top = yCenter - t(h1) / 2;

  const segments: Array<{ x: number; width: number; height: number; fill: string; stroke: string; label: string }> = [];
  let x = MARGIN_X;

  if (n1 === 2) {
    segments.push({
      x,
      width: t(b2),
      height: t(h2),
      fill: REINFORCEMENT_COLOR,
      stroke: REINFORCEMENT_STROKE,
      label: "(2)",
    });
    x += t(b2);
  }
  segments.push({ x, width: t(b1), height: t(h1), fill: EXISTING_COLOR, stroke: EXISTING_STROKE, label: "(1)" });
  x += t(b1);
  segments.push({
    x,
    width: t(b2),
    height: t(h2),
    fill: REINFORCEMENT_COLOR,
    stroke: REINFORCEMENT_STROKE,
    label: "(2)",
  });
  x += t(b2);

  const screwColor = screwOK ? OK_COLOR : BAD_COLOR;
  const screwY = MARGIN_Y + t(h2) - t(eBottom);
  const screwYTop = MARGIN_Y + t(h2) - t(eTop2);

  return (
    <Card style={{ minHeight: 400 }} className={classNames(styles.preview, className)} accentColor="var(--preview-border)" shadow title={i18n.translation.common.preview}>
      <div className={styles.wrapper}>
        {can3D && (
          <div className={styles.viewToggle}>
            <button type="button" className={classNames(styles.viewToggleButton, styles.viewToggleButtonActive)}>
              {i18n.translation.beamReinforcementModule.drawing.view2D}
            </button>
            <button type="button" className={styles.viewToggleButton} onClick={() => setViewMode("3d")}>
              {i18n.translation.beamReinforcementModule.drawing.view3D}
            </button>
          </div>
        )}
        <svg className={styles.svg} viewBox={`0 0 ${VIEW_WIDTH} ${VIEW_HEIGHT}`}>
          {segments.map((segment, index) => {
            const segmentTop = yCenter - segment.height / 2;
            return (
              <g key={`${segment.label}-${index}`}>
                <rect
                  x={segment.x}
                  y={segmentTop}
                  width={segment.width}
                  height={segment.height}
                  fill={segment.fill}
                  stroke={segment.stroke}
                  strokeWidth={1.4}
                />
                <text
                  x={segment.x + segment.width / 2}
                  y={y1Top - 10}
                  textAnchor="middle"
                  fontSize={11}
                  fontWeight={600}
                  fill={DIM_COLOR}
                >
                  {segment.label}
                </text>
              </g>
            );
          })}

          {/* screw axis on the right reinforcement beam */}
          <line
            x1={MARGIN_X - 10}
            y1={screwY}
            x2={VIEW_WIDTH - MARGIN_X + 10}
            y2={screwY}
            stroke={screwColor}
            strokeWidth={1.5}
            strokeDasharray="6 3"
          />
          {n1 === 2 && (
            <line
              x1={MARGIN_X - 10}
              y1={screwYTop}
              x2={VIEW_WIDTH - MARGIN_X + 10}
              y2={screwYTop}
              stroke={screwColor}
              strokeWidth={1.5}
              strokeDasharray="6 3"
            />
          )}
        </svg>

        {!can3D && <div className={styles.hint}>{i18n.translation.beamReinforcementModule.drawing.enable3DHint}</div>}

        <div className={styles.legend}>
          <span>
            <span className={styles.legendSwatch} style={{ background: EXISTING_COLOR }} />
            {`(1) ${result.existing.name}`}
          </span>
          <span>
            <span className={styles.legendSwatch} style={{ background: REINFORCEMENT_COLOR }} />
            {`(2) ${result.reinforcement.name}`}
          </span>
        </div>
      </div>
    </Card>
  );
};

export default SectionDrawingCard;
